import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle, Clock, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { Section } from "./ui";

export function PromoCountdown({ pubSettings, now }) {
  if (!pubSettings?.promo_active || !pubSettings?.promo_end_date) return null;
  const diff = Math.max(0, new Date(pubSettings.promo_end_date).getTime() - now);
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const sec = Math.floor((diff % 60000) / 1000);
  const pad = (n) => String(n).padStart(2, "0");
  const Unit = ({ value, label }) => (
    <span className="inline-flex flex-col items-center leading-none">
      <span className="font-black tabular-nums text-base md:text-lg">{pad(value)}</span>
      <span className="text-[9px] uppercase tracking-wider opacity-80">{label}</span>
    </span>
  );
  return (
    <div data-testid="quote-promo-countdown" className="bg-[#C79A3B] text-white px-6 md:px-12 lg:px-16 py-2.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-sm font-bold text-center">
      <span className="inline-flex items-center gap-2"><Clock size={16} className="shrink-0" /> Pay your deposit today for {Math.round((pubSettings.launch_offer_pct || 0.1) * 100)}% off — offer ends in</span>
      <span className="inline-flex items-center gap-2" data-testid="quote-countdown-timer">
        <Unit value={d} label="days" /><span className="opacity-60">:</span>
        <Unit value={h} label="hrs" /><span className="opacity-60">:</span>
        <Unit value={m} label="min" /><span className="opacity-60">:</span>
        <Unit value={sec} label="sec" />
      </span>
    </div>
  );
}

export function PostcodeStep({ postcode, setPostcode, serviceable, onCheck, onEnquiry, contact, setContact, onContinue }) {
  const contactComplete = !!(contact?.name?.trim() && contact?.email?.trim() && contact?.phone?.trim());
  const handleContinue = () => {
    if (!contactComplete) { toast.error("Your name, email and phone number are required to continue."); return; }
    onContinue();
  };
  return (
    <Section title="Where is the property?" eyebrow="Step 1 · Postcode check">
      <p className="text-[#6B7280] mb-6 max-w-2xl">Instant pricing is available only for single-storey rear extensions inside our serviceable area. If you're outside, send us a few details and we'll provide a tailored quote — no time wasted.</p>
      <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
        <input data-testid="postcode-input" value={postcode} onChange={e => setPostcode(e.target.value)} placeholder="Enter Postcode"
          className="flex-1 border border-[#E5E7EB] bg-white px-4 py-4 focus:outline-none focus:border-[#C79A3B] lowercase" />
        <button data-testid="postcode-check-btn" className="de-btn-primary" onClick={onCheck}>Check <ArrowRight size={16} /></button>
      </div>
      {serviceable === true && (
        <div className="mt-6 border border-[#E5E7EB] p-6 bg-white max-w-2xl" data-testid="postcode-contact-box">
          <div className="flex items-center gap-3 mb-2 text-[#1F6B3B] font-bold"><CheckCircle2 size={18} /> Great news — we cover your area</div>
          <p className="text-sm text-[#6B7280] mb-5">Pop in your details to continue. We'll save your quote under your own reference and email you a copy once it's ready — no need to type them again later.</p>
          <div className="label-eyebrow mb-1">Full name</div>
          <input data-testid="postcode-contact-name" value={contact.name} onChange={e => setContact({ ...contact, name: e.target.value })} placeholder="Jane Smith"
            className="w-full border border-[#E5E7EB] bg-white px-4 py-3 mb-4 text-sm focus:outline-none focus:border-[#C79A3B]" />
          <div className="label-eyebrow mb-1">Phone number</div>
          <input type="tel" data-testid="postcode-contact-phone" value={contact.phone} onChange={e => setContact({ ...contact, phone: e.target.value })} placeholder="07xxx xxxxxx"
            className="w-full border border-[#E5E7EB] bg-white px-4 py-3 mb-4 text-sm focus:outline-none focus:border-[#C79A3B]" />
          <div className="label-eyebrow mb-1">Email</div>
          <input type="email" data-testid="postcode-contact-email" value={contact.email} onChange={e => setContact({ ...contact, email: e.target.value })} placeholder="you@example.com"
            className="w-full border border-[#E5E7EB] bg-white px-4 py-3 mb-5 text-sm focus:outline-none focus:border-[#C79A3B]" />
          <button data-testid="postcode-contact-continue" className="de-btn-primary" onClick={handleContinue}>Continue <ArrowRight size={16} /></button>
          <p className="text-[11px] text-[#9CA3AF] mt-3">We'll only use your details to send your quote and follow up about your project. No spam.</p>
        </div>
      )}
      {serviceable === false && (
        <div className="mt-6 border border-[#E5E7EB] p-6 bg-white max-w-2xl">
          <div className="flex items-center gap-3 mb-3 text-[#C79A3B] font-bold"><AlertTriangle size={18} /> Outside our instant area</div>
          <p className="text-sm text-[#6B7280] mb-4">We still cover a wider area on a bespoke basis. Send your details and we'll come back with a tailored quote.</p>
          <button data-testid="postcode-enquiry-btn" className="de-btn-secondary" onClick={onEnquiry}>Send enquiry</button>
        </div>
      )}
    </Section>
  );
}

export function RouteStep({ onPick }) {
  return (
    <Section title="Which route fits your project?" eyebrow="Step 2 · Route">
      <p className="text-[#6B7280] max-w-2xl mb-8">Most single-storey rear extensions qualify under <b>Permitted Development</b> — no planning application needed, and you can price yours instantly below. Building something larger with full plans? Use the second route. <Link to="/extension-rules" className="de-link font-semibold">See the rules &amp; diagrams</Link>.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl">
        <button data-testid="route-pd" onClick={() => onPick("pd")} className="de-card text-left overflow-hidden border-2 border-[#C79A3B] hover:shadow-lg transition-shadow relative p-0">
          <div className="relative">
            <img src="/images/route-pd-extension.jpg" alt="Completed single-storey rear extension with bifold doors" className="w-full h-44 md:h-52 object-cover" />
            <span className="absolute top-3 left-3 bg-[#C79A3B] text-white text-[10px] font-bold uppercase tracking-widest px-2.5 py-1">Most popular — start here</span>
          </div>
          <div className="p-6 md:p-8">
            <div className="label-eyebrow text-[#C79A3B] mb-3">Permitted Development</div>
            <div className="font-bold text-xl mb-2">Permitted Development</div>
            <div className="text-sm text-[#6B7280]">The route most of our clients take — no planning application needed. We'll run an indicative PD screening. Size capped to standard PD limits. Flat roof only.</div>
          </div>
        </button>
        <button data-testid="route-planning" onClick={() => onPick("planning")} className="de-card text-left overflow-hidden hover:border-[#C79A3B] transition-colors p-0">
          <img src="/images/route-larger-plans.jpg" alt="Architectural plans for a larger extension" className="w-full h-44 md:h-52 object-cover" />
          <div className="p-6 md:p-8">
            <div className="label-eyebrow text-[#C79A3B] mb-3">Planning obtained or prior approval</div>
            <div className="font-bold text-xl mb-1">Larger Extensions</div>
            <div className="text-xs font-semibold text-[#A87E28] mb-2">(only select if you already have drawn plans)</div>
            <div className="text-sm text-[#6B7280]">For full planning permission or a granted Larger Home Extension prior approval. Larger sizes &amp; pitched tiled roof unlocked. We verify before commencement.</div>
          </div>
        </button>
      </div>
    </Section>
  );
}

const FAIL_REASONS = {
  "rear-access-blocked": "We can't get materials and machinery to the rear of the property.",
  "postcode-outside": "Your postcode is outside our current instant-quote area.",
};

export function CriteriaFailModal({ failInfo, onClose, onContact }) {
  if (!failInfo) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="criteria-fail-modal" onClick={onClose}>
      <div className="bg-white rounded-2xl max-w-lg w-full p-8 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="label-eyebrow mb-3 text-[#C79A3B]">A quick heads-up</div>
        <h3 className="text-2xl font-black tracking-tight mb-3">This one needs a human</h3>
        <p className="text-sm text-[#6B7280] mb-4">Based on your answers, your project doesn't qualify for our instant online route for the following reason{failInfo.reasons.length > 1 ? "s" : ""}:</p>
        <ul className="text-sm space-y-2 mb-5">
          {failInfo.reasons.map((r) => (
            <li key={r} className="flex gap-2"><span className="text-[#C79A3B] font-bold">•</span><span>{FAIL_REASONS[r] || r}</span></li>
          ))}
        </ul>
        <p className="text-sm text-[#6B7280] mb-6">That's completely fine — we still build plenty of these. Get in touch and our team will be happy to quote it for you directly.</p>
        <div className="flex flex-wrap gap-3">
          <button data-testid="criteria-fail-contact" className="de-btn-primary" onClick={onContact}>Contact us</button>
          <button data-testid="criteria-fail-close" className="de-btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}
